import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Pill, Calendar, TrendingUp, ChevronRight, Zap, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { listMedications } from '@/lib/medications';
import { getSafetyScore } from '@/lib/interactions';
import { getUpcoming, getAdherence, formatLocalTime, resolveDeviceTimezone } from '@/lib/doses';
import { SeverityColors } from '@/lib/theme';
import SeverityBadge from '@/components/SeverityBadge';

export default function HomePage() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const tz = user?.timezone || resolveDeviceTimezone();

  const [meds, setMeds] = useState([]);
  const [loadingMeds, setLoadingMeds] = useState(true);
  const [score, setScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(true);
  const [scoreError, setScoreError] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [adherence, setAdherence] = useState(null);

  const load = useCallback(async () => {
    try { setMeds(await listMedications()); } catch { setMeds([]); } finally { setLoadingMeds(false); }
    try { const s = await getSafetyScore(); setScore(s); setScoreError(null); } catch (e) { setScoreError(e.message); } finally { setLoadingScore(false); }
    try { setUpcoming(await getUpcoming(24, tz)); } catch { setUpcoming([]); }
    try { setAdherence(await getAdherence(30, tz)); } catch { setAdherence(null); }
  }, [tz]);

  useEffect(() => {
    load();
    if (user && !user.timezone) {
      const device = resolveDeviceTimezone();
      if (device && device !== 'UTC') updateProfile({ timezone: device }).catch(() => {});
    }
  }, [load, user, updateProfile]);

  const colors = score ? SeverityColors[score.worst_severity] : { bg: '#EEF4F7', text: '#4B6B79', border: '#D7E2E8' };
  const enoughMeds = (score?.num_meds ?? 0) >= 2;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="home-screen">
      {/* Greeting */}
      <div className="mb-6 flex items-center gap-4">
        <img src="/brand/logo-round.png" alt="" className="w-12 h-12 rounded-full" data-testid="home-brand-logo" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4B6B79' }}>Welcome back</p>
          <h1 className="text-3xl font-extrabold mt-1" style={{ color: '#5B2D1F' }} data-testid="home-greeting">{user?.name || 'there'}</h1>
        </div>
      </div>

      {/* Safety score card */}
      <button
        onClick={enoughMeds ? () => navigate('/interactions') : undefined}
        disabled={!enoughMeds}
        data-testid="home-safety-score-card"
        className="w-full text-left rounded-2xl border-[1.5px] p-6 mb-4 transition-opacity hover:opacity-90 disabled:cursor-default"
        style={{ backgroundColor: colors.bg, borderColor: colors.border }}
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-full flex items-center justify-center bg-white/70">
            <ShieldCheck className="h-5 w-5" style={{ color: colors.text }} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4B6B79' }}>Safety score</p>
            {loadingScore ? (
              <Loader2 className="h-5 w-5 animate-spin mt-1" style={{ color: '#D77A52' }} />
            ) : (
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className="text-3xl font-extrabold" style={{ color: colors.text }} data-testid="home-safety-score-value">{score?.score ?? '—'}</span>
                <span className="text-base font-bold opacity-70" style={{ color: colors.text }}>/ 100</span>
              </div>
            )}
          </div>
          {!loadingScore && score && enoughMeds && <SeverityBadge severity={score.worst_severity} testId="home-safety-score-severity" />}
        </div>
        {scoreError && <p className="text-xs mt-2" style={{ color: '#B0303A' }} data-testid="home-safety-score-error">{scoreError}</p>}
        {!loadingScore && score && enoughMeds && score.worst_pair && (
          <p className="mt-2 text-base font-medium" style={{ color: colors.text }}>Worst pair: <strong>{score.worst_pair[0]}</strong> × <strong>{score.worst_pair[1]}</strong></p>
        )}
        {!loadingScore && score && !enoughMeds && (
          <p className="mt-2 text-base font-medium" style={{ color: colors.text }}>Add at least two medications to enable interaction analysis.</p>
        )}
        {enoughMeds && (
          <div className="flex items-center justify-end gap-0.5 mt-2">
            <span className="text-xs font-extrabold uppercase tracking-wide" style={{ color: colors.text }}>Tap to view details</span>
            <ChevronRight className="h-4 w-4" style={{ color: colors.text }} />
          </div>
        )}
      </button>

      {/* Run check button */}
      <button
        onClick={() => navigate('/interactions')}
        disabled={!enoughMeds}
        data-testid="home-run-check-btn"
        className="w-full flex items-center justify-center gap-2 py-4 rounded-xl text-base font-extrabold text-white mb-6 transition-opacity hover:opacity-90 disabled:opacity-50"
        style={{ backgroundColor: '#D77A52' }}
      >
        <Zap className="h-5 w-5" />
        Run interaction check now
      </button>

      {/* Medications card */}
      <button onClick={() => navigate('/medications')} data-testid="home-meds-card" className="w-full flex items-center gap-4 p-6 rounded-2xl border mb-4 text-left transition-opacity hover:opacity-90" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
        <div className="w-13 h-13 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EEF4F7', width: 52, height: 52 }}>
          <Pill className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4B6B79' }}>Your medications</p>
          <p className="text-xl font-extrabold mt-0.5" style={{ color: '#5B2D1F' }} data-testid="home-meds-count">{loadingMeds ? '…' : `${meds.length} ${meds.length === 1 ? 'medication' : 'medications'}`}</p>
          <p className="text-xs mt-0.5" style={{ color: '#4B6B79' }}>Tap to view, add, or edit your list.</p>
        </div>
        <ChevronRight className="h-5 w-5" style={{ color: '#4B6B79' }} />
      </button>

      {/* Today card */}
      <button onClick={() => navigate('/today')} data-testid="home-today-card" className="w-full flex items-center gap-4 p-6 rounded-2xl border mb-4 text-left transition-opacity hover:opacity-90" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
        <div className="w-13 h-13 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EEF4F7', width: 52, height: 52 }}>
          <Calendar className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4B6B79' }}>Today&apos;s medications</p>
            <span className="text-xs font-extrabold" style={{ color: '#D77A52' }} data-testid="home-today-view-all">View all →</span>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-xs mt-1" style={{ color: '#4B6B79' }}>Nothing in the next 24 hours.</p>
          ) : (
            <div className="mt-1 space-y-0.5">
              {upcoming.slice(0, 3).map((u, idx) => (
                <div key={`${u.medication_id}-${u.scheduled_for}-${idx}`} className="flex items-center gap-2" data-testid={`home-today-upcoming-${idx}`}>
                  <span className="text-base font-extrabold min-w-[78px]" style={{ color: '#5B2D1F' }}>{formatLocalTime(u.scheduled_for, tz)}</span>
                  <span className="text-base truncate" style={{ color: '#0F2A35' }}>{u.medication_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </button>

      {/* Adherence card */}
      <button onClick={() => navigate('/today')} data-testid="home-adherence-card" className="w-full flex items-center gap-4 p-6 rounded-2xl border mb-6 text-left transition-opacity hover:opacity-90" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
        <div className="w-13 h-13 rounded-full flex items-center justify-center" style={{ backgroundColor: '#EEF4F7', width: 52, height: 52 }}>
          <TrendingUp className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#4B6B79' }}>Adherence — last 30 days</p>
          <p className="text-2xl font-extrabold mt-0.5" style={{ color: '#5B2D1F' }} data-testid="home-adherence-value">{adherence ? `${adherence.adherence_pct}%` : '—'}</p>
          {adherence && <p className="text-xs mt-0.5" style={{ color: '#4B6B79' }}>{adherence.taken} taken · {adherence.missed} missed · {adherence.skipped} skipped</p>}
        </div>
        <ChevronRight className="h-5 w-5" style={{ color: '#4B6B79' }} />
      </button>

      {/* Account info */}
      <div className="rounded-2xl border p-6 mb-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
        <h2 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#4B6B79' }}>Your account</h2>
        <div className="border-t py-2" style={{ borderColor: '#D7E2E8' }}>
          <span className="text-xs font-semibold" style={{ color: '#4B6B79' }}>Email</span>
          <p className="text-base" style={{ color: '#0F2A35' }} data-testid="home-user-email">{user?.email}</p>
        </div>
        {user?.age != null && (
          <div className="border-t py-2" style={{ borderColor: '#D7E2E8' }}>
            <span className="text-xs font-semibold" style={{ color: '#4B6B79' }}>Age</span>
            <p className="text-base" style={{ color: '#0F2A35' }}>{user.age}</p>
          </div>
        )}
      </div>
    </div>
  );
}
